// Aba "Bateria" do detalhe do rastreador: curva da bateria, estado atual com a
// estimativa até o crítico e até acabar, e os ciclos de descarga — quanto tempo
// o aparelho aguenta com o veículo desligado (o TK913 desliga ao zerar e só
// volta no botão). Os ciclos são calculados no ERPApi (SummarizeBattery).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from '@components/Button';
import { Badge } from '@components/Card';
import { formatDateTimeBR } from '@pages/financial/financialUtils';
import { vehicleTrackerService } from '@services/vehicleTrackerService';
import type { TrackerBatteryReport } from '@/types/vehicleTracker';
import { batteryStateLabel, batteryVariant, cycleEndLabel, formatHours, formatRate } from './trackerUtils';

const DAY_OPTIONS = [7, 14, 30, 60];

/** "21/10 20:45" a partir de "2026-10-21T20:45:07" (sem passar por Date). */
const shortDateTime = (iso: string) => {
  const m = /^\d{4}-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  return m ? `${m[2]}/${m[1]} ${m[3]}:${m[4]}` : iso;
};

const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <div className="border border-slate-200 rounded-lg px-3 py-2">
    <div className="text-xs text-slate-500">{label}</div>
    <div className="text-base font-semibold text-slate-900">{value}</div>
    {hint && <div className="text-[11px] text-slate-400">{hint}</div>}
  </div>
);

export const TrackerBatteryPanel: React.FC<{ trackerId: number }> = ({ trackerId }) => {
  const [days, setDays] = useState(7);
  const [report, setReport] = useState<TrackerBatteryReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setReport(await vehicleTrackerService.battery(trackerId, days));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar a bateria');
    } finally {
      setLoading(false);
    }
  }, [trackerId, days]);

  useEffect(() => {
    load();
  }, [load]);

  // O eixo X é o índice da amostra (as amostras vêm só quando a % muda), com o horário no rótulo.
  const chartData = useMemo(
    () => (report?.samples ?? []).map((s) => ({ at: shortDateTime(s.sampled_at), pct: s.battery_percent })),
    [report],
  );

  const summary = report?.summary;
  const limits = report?.limits;
  const state = batteryStateLabel(summary?.state ?? 'unknown');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          Amostra a cada mudança da bateria (ou a cada 1 h). Com o veículo desligado o aparelho dorme e manda só o sinal de vida
          a cada 5 min — é dele que sai a curva de descarga.
        </p>
        <div className="flex items-center gap-2">
          <select
            className="border border-slate-300 rounded-lg px-2 py-1 text-sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="Período"
          >
            {DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>
                Últimos {d} dias
              </option>
            ))}
          </select>
          <Button variant="secondary" size="sm" onClick={load} disabled={loading} icon={<RefreshCw size={14} />}>
            Atualizar
          </Button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}

      {summary && limits && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <Stat label="Situação" value={<Badge variant={state.variant}>{state.label}</Badge>} />
          <Stat
            label="Bateria agora"
            value={
              summary.current_percent != null ? (
                <Badge variant={batteryVariant(summary.current_percent, limits.low_percent, limits.critical_percent)}>
                  {summary.current_percent}%
                </Badge>
              ) : (
                '—'
              )
            }
            hint={`aviso ${limits.low_percent}% · crítico ${limits.critical_percent}%`}
          />
          <Stat label="Descarga atual" value={formatRate(summary.rate_per_hour)} />
          <Stat label={`Até o crítico (${limits.critical_percent}%)`} value={formatHours(summary.hours_to_critical)} hint="na taxa atual" />
          <Stat label="Até acabar (0%)" value={formatHours(summary.hours_to_empty)} hint="na taxa atual" />
          <Stat
            label="Autonomia média (100% → 0%)"
            value={formatHours(summary.avg_autonomy_hours)}
            hint={summary.avg_rate_per_hour != null ? `média ${formatRate(summary.avg_rate_per_hour)}` : 'sem ciclo ≥ 1 h'}
          />
        </div>
      )}

      <div className="h-64 border border-slate-200 rounded-lg p-2">
        {chartData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-slate-500">
            {loading ? 'Carregando...' : 'Nenhuma leitura de bateria no período'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="at" tick={{ fontSize: 11, fill: '#64748b' }} minTickGap={40} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} unit="%" />
              <Tooltip formatter={(v) => [`${v}%`, 'Bateria']} />
              {limits && <ReferenceLine y={limits.low_percent} stroke="#f59e0b" strokeDasharray="4 4" />}
              {limits && <ReferenceLine y={limits.critical_percent} stroke="#ef4444" strokeDasharray="4 4" />}
              <Line type="stepAfter" dataKey="pct" stroke="#059669" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-1">Ciclos de descarga (veículo desligado)</h3>
        <div className="max-h-[30vh] overflow-auto border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-slate-600">
                <th className="px-3 py-2 whitespace-nowrap">Início</th>
                <th className="px-3 py-2 whitespace-nowrap">Fim</th>
                <th className="px-3 py-2 text-right">Bateria</th>
                <th className="px-3 py-2 text-right">Duração</th>
                <th className="px-3 py-2 text-right">Descarga</th>
                <th className="px-3 py-2">Como terminou</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.cycles ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                    {loading ? 'Carregando...' : 'Nenhuma descarga no período'}
                  </td>
                </tr>
              ) : (
                summary!.cycles.map((c) => (
                  <tr key={c.start_at} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 whitespace-nowrap">{formatDateTimeBR(c.start_at)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{formatDateTimeBR(c.end_at)}</td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {c.start_percent}% → {c.end_percent}%
                    </td>
                    <td className="px-3 py-1.5 text-right">{formatHours(c.hours)}</td>
                    <td className="px-3 py-1.5 text-right">{formatRate(c.rate_per_hour)}</td>
                    <td className="px-3 py-1.5">
                      <Badge
                        variant={c.ended_by === 'silent' ? 'danger' : c.ended_by === 'ongoing' ? 'info' : 'neutral'}
                        size="sm"
                      >
                        {cycleEndLabel(c.ended_by)}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
